/**
 * ═══════════════════════════════════════════════════════════════════════════
 * READING A CHART OF SMALL NUMBERS
 *
 * The hours chart had a hard floor of one hour on its axis. The reasoning was
 * sound — without a floor, a week where one person did four minutes draws that
 * day as a full-height bar, which is technically the maximum and a completely
 * false impression of a quiet week.
 *
 * But a fixed floor solves that by making every real week unreadable instead.
 * An organisation doing eighteen minutes draws every bar as a stub against an
 * axis labelled `1h`, and the tallest day and the emptiest look the same.
 *
 * `niceCeiling` keeps the protection and drops the fixed number: the axis is
 * always a round figure somewhat ABOVE the tallest bar, so the peak fills most
 * of the chart without filling all of it. Twelve minutes gets a 15m axis; four
 * minutes gets a 5m axis; three hours gets a 4h axis. Small data reads as
 * small data — you have to read the axis label to know the scale, which is
 * what an axis label is for.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * The round figures an axis is allowed to top out at, in seconds.
 *
 * Deliberately sparse and deliberately familiar. A computed "nicest" ceiling
 * produces axes like `7m 30s`, which is not a number anybody holds in their
 * head while looking at a bar.
 *
 * ── Why every step HALVES into whole minutes ──────────────────────────────
 *
 * The chart prints three marks: the ceiling, its midpoint and zero. A 5-minute
 * ceiling has a midpoint of two and a half, which the formatter rounds to `3m`
 * — an axis whose middle gridline is labelled with a figure it is not at.
 * Small, and exactly the class of quiet inaccuracy this card was full of.
 *
 * So 5 and 15 minutes are not offered, and 10, 20, 30 are. The cost is a
 * little more headroom on the smallest charts; the gain is that no label on
 * this card is approximate.
 */
const STEPS = [
  10 * 60,
  20 * 60,
  30 * 60,
  60 * 60,
  2 * 60 * 60,
  4 * 60 * 60,
  8 * 60 * 60,
  16 * 60 * 60,
  24 * 60 * 60,
] as const;

/**
 * The axis ceiling for a chart whose tallest bar is `peakSeconds`.
 *
 * Always strictly greater than the peak where a step allows it, so the tallest
 * bar stops short of the top — a bar flush with the ceiling reads as clipped,
 * as though the real figure were higher.
 *
 * Above the largest step it rounds up to whole days, because a day of measured
 * learning in one bucket is already past the point where the axis is doing the
 * explaining.
 */
export function niceCeiling(peakSeconds: number): number {
  /* An empty range still needs an axis, and the smallest step is the one that
     makes "nothing happened" look like nothing rather than like a full bar. */
  if (peakSeconds <= 0) return STEPS[0];

  for (const step of STEPS) {
    if (peakSeconds < step) return step;
  }

  /**
   * `floor + 1`, not `ceil`.
   *
   * `Math.ceil` returns the peak itself when the peak is an exact multiple of
   * a day — a bar flush with the top of the chart, which is the one outcome
   * this function exists to prevent. Caught by the sweep in the tests, which
   * is why the sweep runs past the last step rather than stopping at it.
   */
  const day = 24 * 60 * 60;
  return (Math.floor(peakSeconds / day) + 1) * day;
}

/**
 * `6h 45m`, `45m`, `0m` — never a bare decimal, which reads as a score.
 *
 * Seconds in, because every figure behind this card is a sum of
 * `learning_days.seconds` and rounding to hours before formatting is how
 * "eighteen minutes" became "0.31".
 */
export function readableSeconds(seconds: number): string {
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;

  const minutes = Math.round(total / 60);
  if (minutes < 60) return `${minutes}m`;

  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
